import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as Icons from 'lucide-react';
import { ArrowRight, Play, Check, Upload, Sparkles } from 'lucide-react';
import Nav from '../../components/marketing/Nav';
import Footer from '../../components/marketing/Footer';
import { Button } from '../../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { FEATURES, HOW, PRESETS, PLANS, FAQS } from '../../lib/constants';

const HERO_IMG = 'https://images.unsplash.com/photo-1615458318132-1f151a3d18f4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200';

const rise = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06 } }),
};

function Eyebrow({ children }) {
  return <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF4D00] mb-4">{children}</div>;
}

function Hero() {
  const nav = useNavigate();
  const TASKS = ['Shorten video', 'Replace voice', 'Add music', 'Add images / B-roll', 'Add captions'];
  return (
    <section className="relative overflow-hidden grain">
      <div className="absolute inset-0 brand-glow pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 pt-16 pb-20 lg:pt-24 grid lg:grid-cols-2 gap-14 items-center relative">
        <div>
          <motion.div initial="hidden" animate="show" variants={rise}>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/5 px-3 py-1 text-xs text-slate-300">
              <Sparkles className="w-3.5 h-3.5 text-[#8B5CF6]" /> AI Video Transformation Platform
            </span>
          </motion.div>
          <motion.h1 custom={1} initial="hidden" animate="show" variants={rise}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mt-6">
            Turn Any Video Into a <span className="text-gradient">Better YouTube Video.</span>
          </motion.h1>
          <motion.p custom={2} initial="hidden" animate="show" variants={rise}
            className="text-slate-300 text-base sm:text-lg mt-6 max-w-xl leading-relaxed">
            AI-powered editing, voice replacement, music, images, B-roll, captions and automatic time
            reduction — all in one place. Say “make this 24-minute video a polished 10-minute documentary,”
            and the platform handles the work.
          </motion.p>
          <motion.div custom={3} initial="hidden" animate="show" variants={rise} className="flex flex-wrap gap-3 mt-8">
            <Button data-testid="hero-start-btn" onClick={() => nav('/signup')} size="lg" className="rounded-full font-semibold h-12 px-7">
              Start Creating Free <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <Button data-testid="hero-demo-btn" onClick={() => nav('/features')} size="lg" variant="outline"
              className="rounded-full h-12 px-7 border-white/15 bg-white/5 hover:bg-white/10">
              <Play className="w-4 h-4 mr-1" /> Watch Demo
            </Button>
          </motion.div>
          <div className="flex items-center gap-6 mt-8 text-xs text-slate-400 font-mono">
            <span>MP4 · MOV · MKV · WebM</span><span className="text-slate-600">|</span><span>No credit card</span>
          </div>
        </div>

        <motion.div custom={2} initial="hidden" animate="show" variants={rise} className="relative">
          <div className="absolute -inset-4 bg-gradient-to-tr from-[#FF4D00]/20 to-[#8B5CF6]/20 blur-3xl rounded-3xl" />
          <div className="relative glass rounded-2xl p-6 animate-floaty">
            <div className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-3">Create your video</div>
            <div className="rounded-xl border border-dashed border-white/15 bg-[#0B0C12] h-36 grid place-items-center relative overflow-hidden">
              <img src={HERO_IMG} alt="editor" className="absolute inset-0 w-full h-full object-cover opacity-25" />
              <div className="relative text-center">
                <Upload className="w-7 h-7 text-[#FF4D00] mx-auto mb-2" />
                <div className="text-sm text-slate-300">Drop video here <span className="text-slate-500">or upload</span></div>
              </div>
            </div>
            <div className="text-sm text-slate-400 mt-5 mb-3">What do you want AI to do?</div>
            <div className="space-y-2">
              {TASKS.map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-slate-200">
                  <span className="grid place-items-center w-4 h-4 rounded bg-[#FF4D00]"><Check className="w-3 h-3 text-white" /></span>{t}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-5 rounded-lg bg-white/5 px-4 py-3">
              <span className="text-sm text-slate-400">Target length</span>
              <span className="font-mono text-sm text-white">10:00</span>
            </div>
            <Button onClick={() => nav('/signup')} className="w-full mt-4 rounded-full font-semibold h-11">Create Video</Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Stats() {
  const S = [['24:36 → 10:02', 'Auto time reduction'], ['80%', 'Of the edit, done by AI'], ['9', 'Studio AI voices'], ['1-click', 'YouTube package']];
  return (
    <section className="border-y border-white/10 bg-[#0B0C12]">
      <div className="max-w-7xl mx-auto px-5 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
        {S.map(([n, l]) => (
          <div key={l}>
            <div className="font-heading text-2xl sm:text-3xl font-bold text-white">{n}</div>
            <div className="text-sm text-slate-400 mt-1">{l}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function How() {
  return (
    <section className="max-w-7xl mx-auto px-5 py-24">
      <Eyebrow>How it works</Eyebrow>
      <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">Upload. Analyze. Edit. Revoice. Enhance. Publish.</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4 mt-12">
        {HOW.map((s, i) => (
          <motion.div key={s.n} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={rise}
            className="rounded-xl border border-white/10 bg-[#12141D] p-5 hover:border-[#FF4D00]/40 transition-colors">
            <div className="font-mono text-[#8B5CF6] text-sm">{s.n}</div>
            <div className="font-heading font-semibold text-lg mt-3">{s.t}</div>
            <div className="text-sm text-slate-400 mt-1.5 leading-relaxed">{s.d}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function FeaturesGrid() {
  return (
    <section className="max-w-7xl mx-auto px-5 py-12">
      <Eyebrow>AI editing features</Eyebrow>
      <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Everything a finished video needs.</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
        {FEATURES.map((f, i) => {
          const Ico = Icons[f.icon] || Icons.Sparkles;
          return (
            <motion.div key={f.title} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={rise}
              className="group rounded-2xl border border-white/10 bg-[#12141D] p-6 hover:bg-[#161926] transition-colors">
              <div className="grid place-items-center w-11 h-11 rounded-xl bg-gradient-to-br from-[#FF4D00]/20 to-[#8B5CF6]/20 border border-white/10 mb-5">
                <Ico className="w-5 h-5 text-[#FF884D]" />
              </div>
              <div className="font-heading font-semibold text-lg">{f.title}</div>
              <div className="text-sm text-slate-400 mt-2 leading-relaxed">{f.desc}</div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function BeforeAfter() {
  const removed = [['02:11', 'silence'], ['04:35', 'repetition'], ['03:22', 'low-value sections'], ['04:26', 'pacing adjustments']];
  return (
    <section className="max-w-7xl mx-auto px-5 py-24">
      <div className="rounded-3xl border border-white/10 bg-gradient-to-b from-[#12141D] to-[#0B0C12] p-8 sm:p-12">
        <Eyebrow>Before / after</Eyebrow>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-10">See the Edit Decision List in action.</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-white/10 bg-[#0B0C12] p-7">
            <div className="font-mono text-xs uppercase tracking-widest text-slate-500">Original</div>
            <div className="font-heading text-5xl font-extrabold mt-3 text-slate-300">24:36</div>
            <div className="text-sm text-slate-500 mt-2">Raw upload · unedited</div>
          </div>
          <div className="rounded-2xl border border-[#FF4D00]/40 bg-[#0B0C12] p-7 relative overflow-hidden">
            <div className="absolute top-0 right-0 h-full w-[3px] bg-gradient-to-b from-[#FF4D00] to-[#8B5CF6]" />
            <div className="font-mono text-xs uppercase tracking-widest text-[#FF4D00]">Final</div>
            <div className="font-heading text-5xl font-extrabold mt-3 text-gradient">10:02</div>
            <div className="grid grid-cols-2 gap-2 mt-5">
              {removed.map(([t, l]) => (
                <div key={l} className="flex items-center gap-2 text-sm text-slate-400">
                  <span className="font-mono text-slate-300">−{t}</span>{l}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Presets() {
  return (
    <section className="max-w-7xl mx-auto px-5 py-12">
      <Eyebrow>Templates</Eyebrow>
      <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Presets that feel intelligent.</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
        {PRESETS.map((p, i) => (
          <motion.div key={p.name} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={rise}
            className="rounded-2xl border border-white/10 bg-[#12141D] p-6 hover:border-[#8B5CF6]/40 transition-colors">
            <div className="text-3xl mb-3">{p.emoji}</div>
            <div className="font-heading font-semibold text-lg">{p.name}</div>
            <div className="text-sm text-slate-400 mt-1.5">{p.desc}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function PricingPreview() {
  const nav = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-5 py-24">
      <div className="text-center">
        <Eyebrow>Pricing</Eyebrow>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Minutes, not vague “unlimited”.</h2>
        <p className="text-slate-400 mt-3">You always know exactly what you are consuming.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
        {PLANS.map((p) => (
          <div key={p.key} className={`rounded-2xl border p-6 ${p.popular ? 'border-[#FF4D00]/50 bg-[#161926]' : 'border-white/10 bg-[#12141D]'}`}>
            {p.popular && <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF4D00] mb-2">Most popular</div>}
            <div className="font-heading font-semibold text-lg">{p.name}</div>
            <div className="mt-3"><span className="font-heading text-4xl font-extrabold">${p.price}</span><span className="text-slate-500 text-sm">/mo</span></div>
            <div className="text-sm text-slate-400 mt-1">{p.minutes} video minutes</div>
            <Button onClick={() => nav('/signup')} variant={p.popular ? 'default' : 'outline'}
              className={`w-full mt-5 rounded-full ${p.popular ? '' : 'border-white/15 bg-white/5 hover:bg-white/10'}`}>
              Get started
            </Button>
          </div>
        ))}
      </div>
      <div className="text-center mt-8">
        <Link to="/pricing" className="text-sm text-[#FF884D] hover:text-white transition-colors">Compare all features →</Link>
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section className="max-w-3xl mx-auto px-5 py-12">
      <div className="text-center mb-10">
        <Eyebrow>FAQ</Eyebrow>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Questions, answered.</h2>
      </div>
      <Accordion type="single" collapsible className="space-y-3">
        {FAQS.map((f, i) => (
          <AccordionItem key={i} value={`i${i}`} className="rounded-xl border border-white/10 bg-[#12141D] px-5">
            <AccordionTrigger data-testid={`faq-${i}`} className="text-left hover:no-underline">{f.q}</AccordionTrigger>
            <AccordionContent className="text-slate-400 leading-relaxed">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

function CtaBand() {
  const nav = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-5 py-16">
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-r from-[#1a0e08] to-[#140a1f] p-10 sm:p-16 text-center grain">
        <div className="absolute inset-0 brand-glow" />
        <div className="relative">
          <h2 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight">Your first AI edit is free.</h2>
          <p className="text-slate-300 mt-4 max-w-xl mx-auto">Upload a video and watch AI turn it into a finished, YouTube-ready cut.</p>
          <Button data-testid="cta-band-btn" onClick={() => nav('/signup')} size="lg" className="rounded-full h-12 px-8 mt-8 font-semibold">
            Start Creating Free <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#090A0F]">
      <Nav />
      <Hero />
      <Stats />
      <How />
      <FeaturesGrid />
      <BeforeAfter />
      <Presets />
      <PricingPreview />
      <Faq />
      <CtaBand />
      <Footer />
    </div>
  );
}
